import React, { useEffect, useState, useCallback, useRef } from 'react';
import AppShell from '../../components/layout/AppShell';
import { shipmentApi, ticketApi } from '../../services/api';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { StatusBadge, fmtDT, fmtPKR, SERVICE_LABELS, addrStr } from '../../utils/helpers';
import { Spinner, EmptyState, Modal, Pagination } from '../../components/common';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Plus, Search, Bell, Moon, Sun, Upload } from 'lucide-react';

// ── Customer Dashboard ─────────────────────────────────────────────────────────
export function CustomerDashboard() {
  const { user } = useAuth();
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    shipmentApi.getMine({ page:1, limit:5 }).then(res=>{
      if (res.success) setRecent(res.data.shipments||[]);
    }).finally(()=>setLoading(false));
  }, []);
  return (
    <AppShell title="My Dashboard">
      <div className="alert alert-info" style={{ marginBottom:20 }}>
        Welcome back, <strong>{user?.name}</strong>! {user?.type==='B2B'?'Corporate Account':'Individual Customer'}
      </div>
      <div className="stat-grid" style={{ gridTemplateColumns:'repeat(3,1fr)', marginBottom:24 }}>
        <Link to="/customer/book" style={{ textDecoration:'none' }}>
          <div className="stat-card" style={{ cursor:'pointer', textAlign:'center' }} role="link" aria-label="Book a new shipment">
            <div style={{ fontSize:28, marginBottom:8 }}>📦</div>
            <div className="stat-label">Book Shipment</div>
            <div style={{ fontSize:'.8rem', color:'var(--amber)', fontWeight:600 }}>New Booking →</div>
          </div>
        </Link>
        <Link to="/track" style={{ textDecoration:'none' }}>
          <div className="stat-card" style={{ cursor:'pointer', textAlign:'center' }} role="link" aria-label="Track shipment">
            <div style={{ fontSize:28, marginBottom:8 }}>🔍</div>
            <div className="stat-label">Track Shipment</div>
            <div style={{ fontSize:'.8rem', color:'var(--amber)', fontWeight:600 }}>Track Now →</div>
          </div>
        </Link>
        <Link to="/customer/tickets" style={{ textDecoration:'none' }}>
          <div className="stat-card" style={{ cursor:'pointer', textAlign:'center' }} role="link" aria-label="Support tickets">
            <div style={{ fontSize:28, marginBottom:8 }}>🎫</div>
            <div className="stat-label">Support</div>
            <div style={{ fontSize:'.8rem', color:'var(--amber)', fontWeight:600 }}>Get Help →</div>
          </div>
        </Link>
      </div>
      <div className="card">
        <div className="card-header">
          <span className="card-title">Recent Shipments</span>
          <Link to="/customer/shipments" className="btn btn-outline btn-sm">View All</Link>
        </div>
        <div className="table-wrap">
          {loading ? <Spinner /> : recent.length ? (
            <table aria-label="Recent shipments">
              <thead><tr><th>Tracking ID</th><th>Service</th><th>Status</th><th>To</th><th>EDD</th><th>Charges</th></tr></thead>
              <tbody>
                {recent.map(s=>(
                  <tr key={s.tracking_id}>
                    <td><Link to={`/track/${s.tracking_id}`} style={{ color:'var(--navy)', fontWeight:600, fontFamily:'monospace', fontSize:'.82rem' }}>{s.tracking_id}</Link></td>
                    <td>{SERVICE_LABELS[s.service_type]||s.service_type}</td>
                    <td><StatusBadge status={s.status} /></td>
                    <td className="text-sm">{addrStr(s.delivery_address)}</td>
                    <td className="text-sm">{s.edd||'—'}</td>
                    <td>{fmtPKR(s.total_charges)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <EmptyState icon="📦" title="No shipments yet" message={<Link to="/customer/book" className="btn btn-primary btn-sm">Book your first shipment</Link>} />}
        </div>
      </div>
    </AppShell>
  );
}

// ── My Shipments ───────────────────────────────────────────────────────────────
export function MyShipments() {
  const [shipments, setShipments] = useState([]);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await shipmentApi.getMine({ page, limit:20, status:statusFilter||undefined, search:search||undefined });
      if (res.success) { setShipments(res.data.shipments||[]); setPagination(res.pagination||{}); }
    } finally { setLoading(false); }
  }, [page, search, statusFilter]);
  useEffect(()=>{ const t=setTimeout(load,300); return()=>clearTimeout(t); }, [load]);
  return (
    <AppShell title="My Shipments">
      <div className="filters-bar">
        <div className="search-input">
          <Search size={14} aria-hidden="true"/>
          <input aria-label="Search by tracking ID" placeholder="Tracking ID…" value={search} onChange={e=>setSearch(e.target.value)}/>
        </div>
        <select className="form-control" style={{ width:180 }} aria-label="Filter by status" value={statusFilter} onChange={e=>setStatusFilter(e.target.value)}>
          <option value="">All Statuses</option>
          {['BOOKING_CONFIRMED','PICKED_UP','IN_TRANSIT','AT_HUB','OUT_FOR_DELIVERY','DELIVERED','DELIVERY_ATTEMPTED','RTO_INITIATED','CANCELLED'].map(s=>
            <option key={s} value={s}>{s.replace(/_/g,' ')}</option>
          )}
        </select>
      </div>
      <div className="card">
        <div className="table-wrap">
          {loading ? <Spinner /> : shipments.length ? (
            <table aria-label="My shipments">
              <thead><tr><th>Tracking ID</th><th>Service</th><th>Status</th><th>From</th><th>To</th><th>EDD</th><th>COD</th><th>Charges</th></tr></thead>
              <tbody>
                {shipments.map(s=>(
                  <tr key={s.tracking_id}>
                    <td><Link to={`/track/${s.tracking_id}`} style={{ color:'var(--navy)', fontWeight:700, fontFamily:'monospace', fontSize:'.8rem' }}>{s.tracking_id}</Link></td>
                    <td className="text-sm">{SERVICE_LABELS[s.service_type]||s.service_type}</td>
                    <td><StatusBadge status={s.status} /></td>
                    <td className="text-sm">{addrStr(s.pickup_address)}</td>
                    <td className="text-sm">{addrStr(s.delivery_address)}</td>
                    <td className="text-sm">{s.edd||'—'}</td>
                    <td>{s.cod_amount>0?fmtPKR(s.cod_amount):'—'}</td>
                    <td style={{ fontWeight:500 }}>{fmtPKR(s.total_charges)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <EmptyState icon="📦" title="No shipments found" />}
        </div>
        <Pagination page={page} pages={pagination.pages||1} onPage={setPage} />
      </div>
    </AppShell>
  );
}

// ── Book Shipment with ARIA, inline validation, auto-save, EDD preview ─────────
export function BookShipment() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [rate, setRate] = useState(null);
  const [eddPreview, setEddPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rateLoading, setRateLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const autoSaveTimer = useRef(null);
  const DRAFT_KEY = 'ecl_booking_draft';
  const emptyAddr = () => ({ contactName:'', contactPhone:'', line1:'', city:'', country:'PK', pincode:'' });
  const emptyForm = () => ({
    serviceType:'DOM_STD', billingModel:'PREPAID', paymentMethod:'CASH',
    weightActual:'', lengthCm:'', widthCm:'', heightCm:'',
    declaredValue:'0', codAmount:'0', contentDesc:'', specialHandling:[],
    pickupAddress:emptyAddr(), deliveryAddress:emptyAddr(),
  });
  const [form, setForm] = useState(() => { try { return {...emptyForm(),...JSON.parse(localStorage.getItem(DRAFT_KEY)||'{}')}; } catch { return emptyForm(); } });
  const setF = (k,v) => setForm(f=>({...f,[k]:v}));
  const setAddr = (type,k,v) => setForm(f=>({...f,[type]:{...f[type],[k]:v}}));
  const clearErr = (k) => setErrors(e=>{ const n={...e}; delete n[k]; return n; });

  // Auto-save draft every 60s (CP-FE-009)
  useEffect(() => {
    autoSaveTimer.current = setInterval(() => {
      localStorage.setItem(DRAFT_KEY, JSON.stringify(form));
      toast('Draft saved', { icon:'💾', duration:1500, style:{fontSize:'.8rem'} });
    }, 60000);
    return () => clearInterval(autoSaveTimer.current);
  }, [form]);

  // EDD preview after address+service+weight (CP-FE-023)
  useEffect(() => {
    if (!form.weightActual || !form.deliveryAddress?.city) return;
    const t = setTimeout(async () => {
      try {
        const r = await api.post('/ai/predict-edd', { serviceType:form.serviceType, weight:parseFloat(form.weightActual)||1 }).then(r=>r.data);
        if (r.success) setEddPreview(r.data);
      } catch {}
    }, 600);
    return () => clearTimeout(t);
  }, [form.weightActual, form.serviceType, form.deliveryAddress?.city]);

  const validate = () => {
    const e = {};
    if (!form.pickupAddress.line1)   e['pickup.line1']   = 'Pickup address required';
    if (!form.pickupAddress.city)    e['pickup.city']    = 'Pickup city required';
    if (!form.deliveryAddress.line1) e['delivery.line1'] = 'Delivery address required';
    if (!form.deliveryAddress.city)  e['delivery.city']  = 'Delivery city required';
    if (!form.weightActual || parseFloat(form.weightActual)<=0) e['weight'] = 'Valid weight required (kg)';
    if (form.billingModel==='COD' && parseFloat(form.codAmount||0)<=0) e['cod'] = 'COD amount required';
    setErrors(e);
    return !Object.keys(e).length;
  };

  const calcRate = async () => {
    if (!validate()) { toast.error('Please fix the highlighted fields'); return; }
    setRateLoading(true);
    try {
      const res = await shipmentApi.calcRate({ serviceType:form.serviceType, weightActual:parseFloat(form.weightActual),
        lengthCm:form.lengthCm?parseFloat(form.lengthCm):null, widthCm:form.widthCm?parseFloat(form.widthCm):null,
        heightCm:form.heightCm?parseFloat(form.heightCm):null, declaredValue:parseFloat(form.declaredValue||0), codAmount:parseFloat(form.codAmount||0) });
      if (res.success) { setRate(res.data); setStep(2); }
    } catch(e) { toast.error(e.message||'Rate calculation failed'); }
    finally { setRateLoading(false); }
  };

  const confirm = async () => {
    setLoading(true);
    try {
      const res = await shipmentApi.create({
        serviceType:form.serviceType, billingModel:form.billingModel, paymentMethod:form.paymentMethod,
        weightActual:parseFloat(form.weightActual), lengthCm:form.lengthCm?parseFloat(form.lengthCm):null,
        widthCm:form.widthCm?parseFloat(form.widthCm):null, heightCm:form.heightCm?parseFloat(form.heightCm):null,
        declaredValue:parseFloat(form.declaredValue||0), codAmount:parseFloat(form.codAmount||0),
        contentDesc:form.contentDesc, specialHandling:form.specialHandling,
        pickupAddress:form.pickupAddress, deliveryAddress:form.deliveryAddress,
      });
      if (res.success) { localStorage.removeItem(DRAFT_KEY); toast.success('Booked! Confirmation SMS sent.'); navigate(`/track/${res.data.tracking_id}`); }
    } catch(e) { toast.error(e.message||'Booking failed'); }
    finally { setLoading(false); }
  };

  const SERVICES = [
    {value:'DOM_STD',label:'Standard (3 days)'},{value:'DOM_EXP',label:'Express (1 day)'},
    {value:'DOM_SD',label:'Same Day'},{value:'DOM_ECO',label:'Economy (5 days)'},
    {value:'INT_AIR',label:'International Air'},{value:'INT_SEA',label:'International Sea'},
  ];

  return (
    <AppShell title="Book Shipment">
      <div style={{ maxWidth:800, margin:'0 auto' }}>
        <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:24 }} role="navigation" aria-label="Booking steps">
          {['Shipment Details','Review & Confirm'].map((s,i)=>(
            <React.Fragment key={i}>
              <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                <div style={{ width:28,height:28,borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',
                  background:step>i+1?'var(--success)':step===i+1?'var(--amber)':'var(--gray-200)',
                  color:step>=i+1?'#fff':'var(--gray-400)',fontSize:'.8rem',fontWeight:700 }}
                  aria-current={step===i+1?'step':undefined}>{step>i+1?'✓':i+1}</div>
                <span style={{ fontSize:'.85rem', fontWeight:step===i+1?600:400, color:step===i+1?'var(--navy)':'var(--gray-400)' }}>{s}</span>
              </div>
              {i<1 && <div style={{ flex:1,height:2,background:step>1?'var(--amber)':'var(--gray-200)',borderRadius:2 }}/>}
            </React.Fragment>
          ))}
        </div>

        {step===1 && (
          <div style={{ display:'grid', gap:16 }}>
            <div className="form-row">
              <AddrCard title="📍 Pickup" prefix="pickup" addr={form.pickupAddress} errors={errors} onChange={(k,v)=>{setAddr('pickupAddress',k,v);clearErr(`pickup.${k}`);}}/>
              <AddrCard title="📦 Delivery" prefix="delivery" addr={form.deliveryAddress} errors={errors} onChange={(k,v)=>{setAddr('deliveryAddress',k,v);clearErr(`delivery.${k}`);}}/>
            </div>
            {eddPreview && (
              <div className="alert alert-info" style={{ display:'flex', alignItems:'center', gap:10 }}>
                <span>📅</span>
                <div><strong>EDD: {eddPreview.predicted_date}</strong> <span style={{ opacity:.8, fontSize:'.82rem' }}>({eddPreview.range}) · {eddPreview.confidence_pct}% confidence</span></div>
              </div>
            )}
            <div className="card">
              <div className="card-header"><span className="card-title">Service & Package</span></div>
              <div className="card-body">
                <div className="form-row">
                  <FG id="svc" label="Service Type" req>
                    <select id="svc" className="form-control" aria-required="true" value={form.serviceType} onChange={e=>setF('serviceType',e.target.value)}>
                      {SERVICES.map(s=><option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                  </FG>
                  <FG id="billing" label="Billing Model">
                    <select id="billing" className="form-control" value={form.billingModel} onChange={e=>setF('billingModel',e.target.value)}>
                      <option value="PREPAID">Prepaid</option><option value="COD">Cash on Delivery</option>
                    </select>
                  </FG>
                </div>
                <div className="form-row">
                  <FG id="weight" label="Weight (kg)" req error={errors.weight}>
                    <input id="weight" className={`form-control${errors.weight?' is-invalid':''}`} type="number" step="0.001" min="0.001" placeholder="0.500"
                      aria-required="true" aria-describedby={errors.weight?'weight-err':undefined}
                      value={form.weightActual} onChange={e=>{setF('weightActual',e.target.value);clearErr('weight');}} />
                  </FG>
                  <div className="form-group">
                    <label className="form-label">Dimensions (cm)</label>
                    <div style={{ display:'flex', gap:6 }}>
                      {['lengthCm','widthCm','heightCm'].map((k,i)=>(
                        <input key={k} className="form-control" type="number" placeholder={['L','W','H'][i]} aria-label={['Length','Width','Height'][i]} value={form[k]} onChange={e=>setF(k,e.target.value)}/>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="form-row">
                  <FG id="dv" label="Declared Value (PKR)">
                    <input id="dv" className="form-control" type="number" min="0" value={form.declaredValue} onChange={e=>setF('declaredValue',e.target.value)}/>
                  </FG>
                  {form.billingModel==='COD' && (
                    <FG id="cod" label="COD Amount (PKR)" req error={errors.cod}>
                      <input id="cod" className={`form-control${errors.cod?' is-invalid':''}`} type="number" min="0"
                        aria-required="true" aria-describedby={errors.cod?'cod-err':undefined}
                        value={form.codAmount} onChange={e=>{setF('codAmount',e.target.value);clearErr('cod');}}/>
                    </FG>
                  )}
                </div>
                <FG id="content" label="Content Description">
                  <input id="content" className="form-control" placeholder="e.g. Clothing, Documents" value={form.contentDesc} onChange={e=>setF('contentDesc',e.target.value)}/>
                </FG>
              </div>
            </div>
            <button className="btn btn-primary btn-lg w-full" onClick={calcRate} disabled={rateLoading} aria-busy={rateLoading}>
              {rateLoading?'Calculating…':'Calculate Rate & Continue →'}
            </button>
          </div>
        )}

        {step===2 && rate && (
          <div style={{ display:'grid', gap:16 }}>
            <div className="card">
              <div className="card-header"><span className="card-title">Rate Breakdown</span></div>
              <div className="card-body">
                {[['Base Freight',rate.baseFreight],['Fuel Surcharge',rate.fuelSurcharge],['Insurance',rate.insuranceFee],['COD Fee',rate.codFee],['Tax (GST 18%)',rate.taxAmount]].filter(([,v])=>v>0).map(([l,v])=>(
                  <div key={l} className="flex-between" style={{ padding:'6px 0', borderBottom:'1px solid var(--gray-100)' }}>
                    <span style={{ fontSize:'.85rem', color:'var(--gray-600)' }}>{l}</span>
                    <span style={{ fontSize:'.85rem' }}>{fmtPKR(v)}</span>
                  </div>
                ))}
                <div className="flex-between" style={{ paddingTop:10, borderTop:'2px solid var(--navy)' }}>
                  <strong>Total</strong>
                  <strong style={{ fontSize:'1.15rem', color:'var(--navy)' }}>{fmtPKR(rate.totalCharges)}</strong>
                </div>
                {eddPreview && <div style={{ marginTop:12, padding:10, background:'var(--gray-50)', borderRadius:6, fontSize:'.82rem' }}>EDD: <strong>{eddPreview.predicted_date}</strong> ({eddPreview.range})</div>}
              </div>
            </div>
            <div style={{ display:'flex', gap:12 }}>
              <button className="btn btn-outline" onClick={()=>setStep(1)}>← Back</button>
              <button className="btn btn-amber btn-lg" style={{ flex:1 }} onClick={confirm} disabled={loading} aria-busy={loading}>
                {loading?'Confirming…':`Confirm — ${fmtPKR(rate.totalCharges)}`}
              </button>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}

// ── Customer Tickets with 6 categories, char limit, attachments, TKT format ────
export function CustomerTickets() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [newMsg, setNewMsg] = useState('');
  const [charCount, setCharCount] = useState(0);
  const [form, setForm] = useState({ subject:'', description:'', category:'DELIVERY_ISSUE', priority:'MEDIUM', trackingId:'', attachments:[] });
  const [formErrors, setFormErrors] = useState({});
  const fileRef = useRef(null);
  const MAX_CHARS = 2000;
  const MAX_FILES = 5;

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await ticketApi.getMine({ page:1, limit:50 }); if (r.success) setTickets(r.data); }
    finally { setLoading(false); }
  }, []);
  useEffect(()=>{ load(); }, [load]);

  const openDetail = async (t) => {
    setSelected(t);
    const r = await ticketApi.getOne(t.ticket_id);
    if (r.success) setDetail(r.data);
  };

  const validateCreate = () => {
    const e = {};
    if (!form.subject.trim()) e.subject = 'Subject is required';
    if (!form.description.trim()) e.description = 'Description is required';
    setFormErrors(e);
    return !Object.keys(e).length;
  };

  const createTicket = async () => {
    if (!validateCreate()) return;
    try {
      const r = await ticketApi.create(form);
      if (r.success) { toast.success(`Ticket ${r.data.ticket_no} created. Auto-acknowledgment sent.`); setShowCreate(false); setForm({ subject:'', description:'', category:'DELIVERY_ISSUE', priority:'MEDIUM', trackingId:'', attachments:[] }); load(); }
    } catch(e) { toast.error(e.message||'Failed'); }
  };

  const sendMsg = async () => {
    if (!newMsg.trim()) return;
    try {
      await ticketApi.addMessage(detail.ticket.ticket_id, { message:newMsg });
      setNewMsg('');
      const r = await ticketApi.getOne(detail.ticket.ticket_id);
      if (r.success) setDetail(r.data);
    } catch { toast.error('Failed'); }
  };

  const CATEGORIES = ['DELIVERY_ISSUE','DAMAGE_CLAIM','LOST_SHIPMENT','BILLING_QUERY','PICKUP_ISSUE','OTHER'];
  const PRIORITIES = ['LOW','MEDIUM','HIGH','URGENT'];
  const SB = { OPEN:'badge-danger', IN_PROGRESS:'badge-warning', PENDING_CUSTOMER:'badge-info', RESOLVED:'badge-success', CLOSED:'badge-gray' };

  return (
    <AppShell title="Support Tickets" actions={<button className="btn btn-primary btn-sm" onClick={()=>setShowCreate(true)}><Plus size={14}/> New Ticket</button>}>
      <div className="card">
        <div className="table-wrap">
          {loading ? <Spinner /> : tickets.length ? (
            <table aria-label="My support tickets">
              <thead><tr><th>Ticket No</th><th>Subject</th><th>Category</th><th>Priority</th><th>SLA</th><th>Status</th><th></th></tr></thead>
              <tbody>
                {tickets.map(t=>(
                  <tr key={t.ticket_id}>
                    <td><code style={{ fontSize:'.78rem', fontWeight:700 }}>{t.ticket_no}</code></td>
                    <td style={{ fontWeight:500, maxWidth:180 }}><span className="truncate" style={{ display:'block' }}>{t.subject}</span></td>
                    <td><span className="badge badge-info" style={{ fontSize:'.68rem' }}>{t.category?.replace(/_/g,' ')}</span></td>
                    <td><span className={`badge ${t.priority==='URGENT'?'badge-danger':t.priority==='HIGH'?'badge-warning':'badge-info'}`} style={{ fontSize:'.68rem' }}>{t.priority}</span></td>
                    <td className="text-sm" style={{ color:t.sla_deadline&&new Date(t.sla_deadline)<new Date()&&!['RESOLVED','CLOSED'].includes(t.status)?'var(--danger)':undefined }}>
                      {t.sla_deadline?fmtDT(t.sla_deadline):'—'}
                    </td>
                    <td><span className={`badge ${SB[t.status]||'badge-gray'}`}>{t.status?.replace(/_/g,' ')}</span></td>
                    <td><button className="btn btn-outline btn-sm" onClick={()=>openDetail(t)}>View</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <EmptyState icon="🎫" title="No tickets yet" message={<button className="btn btn-primary btn-sm" onClick={()=>setShowCreate(true)}>Create Ticket</button>}/>}
        </div>
      </div>

      <Modal open={showCreate} onClose={()=>setShowCreate(false)} title="New Support Ticket">
        <div className="modal-body">
          <FG id="tkt-tid" label="Tracking ID (optional)">
            <input id="tkt-tid" className="form-control" placeholder="ECLPK…" value={form.trackingId} onChange={e=>setForm(f=>({...f,trackingId:e.target.value}))}/>
          </FG>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label" htmlFor="tkt-cat">Category <span className="req" aria-hidden="true">*</span></label>
              <select id="tkt-cat" className="form-control" aria-required="true" value={form.category} onChange={e=>setForm(f=>({...f,category:e.target.value}))}>
                {CATEGORIES.map(c=><option key={c} value={c}>{c.replace(/_/g,' ')}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="tkt-pri">Priority</label>
              <select id="tkt-pri" className="form-control" value={form.priority} onChange={e=>setForm(f=>({...f,priority:e.target.value}))}>
                {PRIORITIES.map(p=><option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          </div>
          <FG id="tkt-sub" label="Subject" req error={formErrors.subject}>
            <input id="tkt-sub" className={`form-control${formErrors.subject?' is-invalid':''}`} aria-required="true" placeholder="Brief summary"
              aria-describedby={formErrors.subject?'tkt-sub-err':undefined}
              value={form.subject} onChange={e=>{setForm(f=>({...f,subject:e.target.value}));setFormErrors(e=>({...e,subject:undefined}));}}/>
          </FG>
          <div className="form-group">
            <label className="form-label" htmlFor="tkt-desc">
              Description <span className="req" aria-hidden="true">*</span>
              <span style={{ float:'right', fontSize:'.72rem', color:charCount>1800?'var(--danger)':'var(--gray-400)' }}>{charCount}/{MAX_CHARS}</span>
            </label>
            <textarea id="tkt-desc" className={`form-control${formErrors.description?' is-invalid':''}`} rows={5} aria-required="true"
              maxLength={MAX_CHARS} aria-describedby={formErrors.description?'tkt-desc-err':undefined}
              value={form.description}
              onChange={e=>{setForm(f=>({...f,description:e.target.value}));setCharCount(e.target.value.length);setFormErrors(er=>({...er,description:undefined}));}}/>
            {formErrors.description && <div id="tkt-desc-err" role="alert" style={{ fontSize:'.78rem', color:'var(--danger)', marginTop:3 }}>{formErrors.description}</div>}
          </div>
          <div className="form-group">
            <label className="form-label">Attachments <span style={{ fontSize:'.72rem', color:'var(--gray-400)' }}>(max {MAX_FILES} files, 10 MB each)</span></label>
            <button type="button" className="btn btn-outline btn-sm" onClick={()=>fileRef.current?.click()} aria-label="Attach files">
              <Upload size={13}/> Add Files {form.attachments.length>0 && `(${form.attachments.length})`}
            </button>
            <input ref={fileRef} type="file" multiple accept="image/*,.pdf,.doc,.docx" style={{ display:'none' }} aria-label="File input"
              onChange={e=>{const files=Array.from(e.target.files).slice(0,MAX_FILES);setForm(f=>({...f,attachments:files.map(f=>f.name)}));toast.success(`${files.length} file(s) attached`);}}/>
            {form.attachments.length>0 && <div style={{ marginTop:6, fontSize:'.78rem', color:'var(--gray-500)' }}>{form.attachments.map((f,i)=><span key={i} style={{ marginRight:8 }}>📎 {f}</span>)}</div>}
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={()=>setShowCreate(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={createTicket}>Submit Ticket</button>
        </div>
      </Modal>

      <Modal open={!!selected} onClose={()=>{setSelected(null);setDetail(null);}} title={selected?.ticket_no} size="modal-lg">
        {detail ? (
          <div className="modal-body">
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12, padding:12, background:'var(--gray-50)', borderRadius:8, marginBottom:16, fontSize:'.82rem' }}>
              <div><span style={{ color:'var(--gray-400)' }}>Category:</span> {detail.ticket.category?.replace(/_/g,' ')}</div>
              <div><span style={{ color:'var(--gray-400)' }}>Priority:</span> {detail.ticket.priority}</div>
              <div><span style={{ color:'var(--gray-400)' }}>SLA:</span> {fmtDT(detail.ticket.sla_deadline)}</div>
            </div>
            <div style={{ maxHeight:320, overflowY:'auto', display:'flex', flexDirection:'column', gap:8, marginBottom:12 }} aria-live="polite" aria-label="Ticket messages">
              {detail.messages.map(m=>(
                <div key={m.message_id} style={{ alignSelf:m.actor_type==='customer'?'flex-end':'flex-start', maxWidth:'80%', padding:'8px 12px', borderRadius:12,
                  background:m.actor_type==='customer'?'var(--navy)':'var(--gray-100)',
                  color:m.actor_type==='customer'?'#fff':'var(--gray-800)', fontSize:'.85rem' }}>
                  <div style={{ fontWeight:600, fontSize:'.72rem', marginBottom:4, opacity:.8 }}>
                    {m.actor_type==='customer'?'You':m.actor_type==='agent'?'ECL Agent':'Bot'} · {fmtDT(m.sent_at)}
                  </div>
                  {m.message_text}
                </div>
              ))}
            </div>
            {!['RESOLVED','CLOSED'].includes(detail.ticket.status) && (
              <div style={{ display:'flex', gap:8 }}>
                <input className="form-control" aria-label="Type your reply" placeholder="Reply…"
                  value={newMsg} onChange={e=>setNewMsg(e.target.value)} onKeyDown={e=>e.key==='Enter'&&!e.shiftKey&&sendMsg()}/>
                <button className="btn btn-primary btn-sm" onClick={sendMsg}>Send</button>
              </div>
            )}
          </div>
        ) : <div className="loading-center" style={{ height:200 }}><div className="spinner"/></div>}
      </Modal>
    </AppShell>
  );
}

// ── Notification Preferences (CP-FE-038) ──────────────────────────────────────
export function NotifPreferencesPage() {
  const [prefs, setPrefs] = useState({ notif_sms:true, notif_email:true, notif_whatsapp:false, notif_push:true, notif_language:'en', dark_mode:false });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get('/customer/notif-prefs').then(r=>{ if(r.data.success && r.data.data) setPrefs(r.data.data); }).catch(()=>{}).finally(()=>setLoading(false));
  }, []);
  const save = async () => {
    setSaving(true);
    try {
      await api.patch('/customer/notif-prefs', { notifSms:prefs.notif_sms, notifEmail:prefs.notif_email, notifWhatsapp:prefs.notif_whatsapp, notifPush:prefs.notif_push, notifLanguage:prefs.notif_language, darkMode:prefs.dark_mode });
      toast.success('Preferences saved');
      document.documentElement.setAttribute('data-theme', prefs.dark_mode?'dark':'light');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };
  const toggle = k => setPrefs(p=>({...p,[k]:!p[k]}));
  if (loading) return <AppShell title="Preferences"><Spinner /></AppShell>;
  return (
    <AppShell title="Notification Preferences">
      <div style={{ maxWidth:520 }}>
        <div className="card" style={{ marginBottom:16 }}>
          <div className="card-header"><span className="card-title"><Bell size={14} style={{ marginRight:6, display:'inline' }}/>Notification Channels</span></div>
          <div className="card-body">
            {[{ k:'notif_sms', l:'SMS notifications', d:'Status updates via SMS' },
              { k:'notif_email', l:'Email notifications', d:'Confirmations, invoices, updates' },
              { k:'notif_push', l:'Push notifications', d:'Real-time browser/app alerts' },
              { k:'notif_whatsapp', l:'WhatsApp notifications', d:'Status updates via WhatsApp' }].map(ch=>(
              <div key={ch.k} className="flex-between" style={{ padding:'12px 0', borderBottom:'1px solid var(--gray-100)' }}>
                <div>
                  <div style={{ fontWeight:500, fontSize:'.9rem' }}>{ch.l}</div>
                  <div style={{ fontSize:'.75rem', color:'var(--gray-400)' }}>{ch.d}</div>
                </div>
                <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer' }} aria-label={`Toggle ${ch.l}`}>
                  <div style={{ width:36, height:20, borderRadius:10, background:prefs[ch.k]?'var(--navy)':'var(--gray-300)', position:'relative', transition:'background .2s', cursor:'pointer' }}
                    onClick={()=>toggle(ch.k)} role="switch" aria-checked={!!prefs[ch.k]} tabIndex={0}
                    onKeyDown={e=>(e.key==='Enter'||e.key===' ')&&toggle(ch.k)}>
                    <div style={{ position:'absolute', top:2, left:prefs[ch.k]?16:2, width:16, height:16, borderRadius:'50%', background:'#fff', transition:'left .2s' }}/>
                  </div>
                </label>
              </div>
            ))}
          </div>
        </div>
        <div className="card" style={{ marginBottom:16 }}>
          <div className="card-header"><span className="card-title">Language & Theme</span></div>
          <div className="card-body">
            <FG id="lang" label="Notification Language">
              <select id="lang" className="form-control" value={prefs.notif_language} onChange={e=>setPrefs(p=>({...p,notif_language:e.target.value}))}>
                <option value="en">English</option><option value="ur">اردو (Urdu)</option>
                <option value="ar">العربية (Arabic)</option><option value="hi">हिन्दी (Hindi)</option><option value="es">Español</option>
              </select>
            </FG>
            <div className="flex-between" style={{ paddingTop:12 }}>
              <div>
                <div style={{ fontWeight:500 }}>Dark mode</div>
                <div style={{ fontSize:'.75rem', color:'var(--gray-400)' }}>Toggle dark/light interface</div>
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                {prefs.dark_mode ? <Moon size={16}/> : <Sun size={16}/>}
                <div style={{ width:36, height:20, borderRadius:10, background:prefs.dark_mode?'var(--navy)':'var(--gray-300)', position:'relative', transition:'background .2s', cursor:'pointer' }}
                  onClick={()=>toggle('dark_mode')} role="switch" aria-checked={!!prefs.dark_mode} tabIndex={0}
                  onKeyDown={e=>(e.key==='Enter'||e.key===' ')&&toggle('dark_mode')}>
                  <div style={{ position:'absolute', top:2, left:prefs.dark_mode?16:2, width:16, height:16, borderRadius:'50%', background:'#fff', transition:'left .2s' }}/>
                </div>
              </div>
            </div>
          </div>
        </div>
        <button className="btn btn-primary w-full" onClick={save} disabled={saving} aria-busy={saving}>{saving?'Saving…':'Save Preferences'}</button>
      </div>
    </AppShell>
  );
}

// ── Bulk CSV Upload (CP-FE-027) ────────────────────────────────────────────────
export function BulkBookingPage() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState(null);
  const fileRef = useRef(null);

  const parseCSV = (text) => {
    const lines = text.trim().split('\n');
    const headers = lines[0].split(',').map(h=>h.trim().toLowerCase().replace(/[\s-]/g,'_'));
    return lines.slice(1).filter(l=>l.trim()).map((line,idx)=>{
      const vals = line.split(',').map(v=>v.trim().replace(/^"|"$/g,''));
      const row = { _row:idx+2 };
      headers.forEach((h,i)=>{ row[h]=vals[i]; });
      return { _row:idx+2, serviceType:row.service_type||'DOM_STD', billingModel:row.billing_model||'PREPAID',
        weightActual:parseFloat(row.weight_kg||row.weight||0.5), codAmount:parseFloat(row.cod_amount||0),
        declaredValue:parseFloat(row.declared_value||0), contentDesc:row.content||'',
        pickupAddress:{ line1:row.pickup_address||'', city:row.pickup_city||'', country:'PK' },
        deliveryAddress:{ line1:row.delivery_address||row.delivery||'', city:row.delivery_city||row.city||'',
          contactName:row.receiver_name||row.name||'', contactPhone:row.receiver_phone||row.phone||'', country:'PK' } };
    }).filter(r=>r.deliveryAddress.city);
  };

  const handleFile = e => {
    const f = e.target.files[0]; if (!f) return; setFile(f);
    const reader = new FileReader();
    reader.onload = ev => setPreview(parseCSV(ev.target.result).slice(0,5));
    reader.readAsText(f);
  };

  const submit = async () => {
    if (!file) return; setUploading(true);
    const reader = new FileReader();
    reader.onload = async ev => {
      try {
        const rows = parseCSV(ev.target.result);
        const r = await api.post('/shipments/bulk', { rows }).then(r=>r.data);
        if (r.success) { setResults(r.data); toast.success(`${r.data.success}/${r.data.total} booked`); }
      } catch(e) { toast.error(e.response?.data?.message||'Upload failed'); }
      finally { setUploading(false); }
    };
    reader.readAsText(file);
  };

  return (
    <AppShell title="Bulk Booking Upload">
      <div style={{ maxWidth:700 }}>
        <div className="card" style={{ marginBottom:16 }}>
          <div className="card-header"><span className="card-title">Upload CSV File</span></div>
          <div className="card-body">
            <div className="alert alert-info" style={{ marginBottom:12 }}>
              <strong>Required columns:</strong> service_type, weight_kg, delivery_city, delivery_address, receiver_name, receiver_phone<br/>
              <strong>Optional:</strong> billing_model, cod_amount, declared_value, pickup_city, pickup_address, content
            </div>
            <div style={{ border:'2px dashed var(--gray-300)', borderRadius:8, padding:32, textAlign:'center', cursor:'pointer' }}
              onClick={()=>fileRef.current?.click()} role="button" aria-label="Select CSV file"
              onDragOver={e=>e.preventDefault()} onDrop={e=>{e.preventDefault();const f=e.dataTransfer.files[0];if(f){setFile(f);const r=new FileReader();r.onload=ev=>setPreview(parseCSV(ev.target.result).slice(0,5));r.readAsText(f);}}}>
              <Upload size={28} style={{ color:'var(--gray-400)', marginBottom:8 }}/>
              <div style={{ fontWeight:500 }}>{file?file.name:'Drop CSV here or click to browse'}</div>
              <div style={{ fontSize:'.78rem', color:'var(--gray-400)', marginTop:4 }}>Max 1,000 shipments per file</div>
            </div>
            <input ref={fileRef} type="file" accept=".csv" style={{ display:'none' }} onChange={handleFile} aria-label="CSV file input"/>
            {preview.length>0 && (
              <div style={{ marginTop:12 }}>
                <div style={{ fontWeight:600, marginBottom:8, fontSize:'.85rem' }}>Preview ({preview.length} rows):</div>
                <div className="table-wrap">
                  <table><thead><tr><th>#</th><th>Service</th><th>Weight</th><th>Delivery City</th><th>Receiver</th><th>COD</th></tr></thead>
                    <tbody>{preview.map(r=>(
                      <tr key={r._row}><td style={{ fontSize:'.75rem' }}>{r._row}</td><td className="text-sm">{r.serviceType}</td>
                        <td className="text-sm">{r.weightActual}kg</td><td className="text-sm">{r.deliveryAddress.city}</td>
                        <td className="text-sm">{r.deliveryAddress.contactName}</td><td className="text-sm">{r.codAmount>0?fmtPKR(r.codAmount):'—'}</td></tr>
                    ))}</tbody>
                  </table>
                </div>
              </div>
            )}
            <button className="btn btn-primary w-full" style={{ marginTop:12 }} onClick={submit} disabled={!file||uploading} aria-busy={uploading}>
              {uploading?'Uploading…':'Upload & Book All'}
            </button>
          </div>
        </div>
        {results && (
          <div className="card">
            <div className="card-header">
              <span className="card-title">Results</span>
              <span style={{ fontSize:'.8rem' }}><span style={{ color:'var(--success)' }}>✓ {results.success} success</span>{results.errors>0&&<span style={{ color:'var(--danger)', marginLeft:8 }}>✗ {results.errors} errors</span>}</span>
            </div>
            <div className="table-wrap">
              <table><thead><tr><th>Row</th><th>Status</th><th>Tracking ID</th><th>Total</th><th>Error</th></tr></thead>
                <tbody>{results.results.map(r=>(
                  <tr key={r.row}><td style={{ fontSize:'.78rem' }}>{r.row}</td>
                    <td><span className={`badge ${r.success?'badge-success':'badge-danger'}`}>{r.success?'OK':'FAIL'}</span></td>
                    <td><code style={{ fontSize:'.75rem' }}>{r.trackingId||'—'}</code></td>
                    <td className="text-sm">{r.total?fmtPKR(r.total):'—'}</td>
                    <td style={{ fontSize:'.78rem', color:'var(--danger)' }}>{r.error||'—'}</td></tr>
                ))}</tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}

// ── Shared field group ─────────────────────────────────────────────────────────
function FG({ id, label, req, error, children }) {
  return (
    <div className="form-group">
      <label className="form-label" htmlFor={id}>{label}{req&&<span className="req" aria-hidden="true"> *</span>}</label>
      {children}
      {error && <div id={`${id}-err`} role="alert" style={{ fontSize:'.78rem', color:'var(--danger)', marginTop:3 }}>{error}</div>}
    </div>
  );
}

function AddrCard({ title, prefix, addr, errors, onChange }) {
  const ef = k => errors[`${prefix}.${k}`];
  return (
    <div className="card">
      <div className="card-header"><span className="card-title">{title}</span></div>
      <div className="card-body">
        <FG id={`${prefix}-name`} label="Contact Name"><input id={`${prefix}-name`} className="form-control" placeholder="Full name" aria-label={`${title} contact name`} value={addr.contactName} onChange={e=>onChange('contactName',e.target.value)}/></FG>
        <FG id={`${prefix}-phone`} label="Phone"><input id={`${prefix}-phone`} className="form-control" placeholder="+92 300..." aria-label={`${title} phone`} value={addr.contactPhone} onChange={e=>onChange('contactPhone',e.target.value)}/></FG>
        <FG id={`${prefix}-line1`} label="Address" req error={ef('line1')}>
          <input id={`${prefix}-line1`} className={`form-control${ef('line1')?' is-invalid':''}`} placeholder="Street / Area" aria-required="true" aria-describedby={ef('line1')?`${prefix}-line1-err`:undefined} value={addr.line1} onChange={e=>onChange('line1',e.target.value)}/>
        </FG>
        <div className="form-row">
          <FG id={`${prefix}-city`} label="City" req error={ef('city')}>
            <input id={`${prefix}-city`} className={`form-control${ef('city')?' is-invalid':''}`} placeholder="Karachi" aria-required="true" aria-describedby={ef('city')?`${prefix}-city-err`:undefined} value={addr.city} onChange={e=>onChange('city',e.target.value)}/>
          </FG>
          <FG id={`${prefix}-pin`} label="Pincode"><input id={`${prefix}-pin`} className="form-control" placeholder="75500" aria-label="Postal code" value={addr.pincode} onChange={e=>onChange('pincode',e.target.value)}/></FG>
        </div>
      </div>
    </div>
  );
}
